import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ConstantsService } from '../fidelidade/ConstantsService';
import { CardapioAnalyticsService } from './cardapio-analytics.service';
import { Subscription } from 'rxjs';

declare var moment: any;

@Component({
  selector: 'app-analytics',
  templateUrl: './analytics.component.html',
  styleUrls: ['./analytics.component.scss']
})
export class AnalyticsComponent implements OnInit, OnDestroy {
  empresa: any = {};
  status: 'carregando' | 'sem_projeto' | 'erro' | 'pronto' = 'carregando';
  carregando = false;
  erroMensagem = '';

  // Filtro
  quickDateFilter: 'hoje' | '7dias' | '14dias' | '30dias' | '90dias' | 'personalizado' = '30dias';
  filtroInicio: Date;
  filtroFim: Date;

  // Dados do backend
  dashboard: any = null;
  analyticsResumo: any = null;
  resumoFunil: any = null;
  steps: any[] = [];
  comparativo: any = null;
  serieTemporal: any = null;
  produtos: any[] = [];
  campanhas: any = {};
  alertas: any[] = [];
  metadados: any = {};

  // Derivados
  bannerResumo = '';
  gargalos: any[] = [];
  topVisualizacoes: any[] = [];
  topSacola: any[] = [];
  topPedidos: any[] = [];
  openpanelDashboardIframe: SafeResourceUrl | null = null;
  openpanelRealtimeIframe: SafeResourceUrl | null = null;
  openpanelDashboardUrl = '';
  openpanelRealtimeUrl = '';
  seriesConversao: Array<number | null> = [];
  seriesConversaoPlot: number[] = [];
  categoriesConversao: string[] = [];
  rotulosConversao = {
    rotation: 0,
    font: '11px sans-serif',
    color: '#94a3b8',
    content: (e: any) => this.obtenhaConteudoRotuloConversao(e)
  };

  private sub: Subscription;

  constructor(
    private constantsService: ConstantsService,
    private analyticsService: CardapioAnalyticsService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.aplicarPreset('30dias');
    this.sub = this.constantsService.empresa$.subscribe((empresa: any) => {
      if (!empresa) return;
      this.empresa = empresa;
      this.atualizeIframesOpenpanel(empresa);
      this.verifique(empresa);
    });
  }

  ngOnDestroy(): void {
    if (this.sub) this.sub.unsubscribe();
  }

  private async verifique(empresa: any): Promise<void> {
    this.status = 'carregando';
    this.erroMensagem = '';
    await this.carregueDashboard();
  }

  periodos = [
    { valor: 'hoje', label: 'Hoje' },
    { valor: '7dias', label: '7 dias' },
    { valor: '14dias', label: '14 dias' },
    { valor: '30dias', label: '30 dias' },
    { valor: '90dias', label: '90 dias' }
  ];

  selecionePreset(preset: 'hoje' | '7dias' | '14dias' | '30dias' | '90dias' | string): void {
    this.quickDateFilter = preset as any;
    this.aplicarPreset(preset);
    this.carregueDashboard();
  }

  private aplicarPreset(preset: string): void {
    if (preset === 'hoje') {
      this.filtroInicio = moment().startOf('day').toDate();
      this.filtroFim = moment().endOf('day').toDate();
    } else if (preset === '7dias') {
      this.filtroInicio = moment().subtract(6, 'days').startOf('day').toDate();
      this.filtroFim = moment().endOf('day').toDate();
    } else if (preset === '14dias') {
      this.filtroInicio = moment().subtract(13, 'days').startOf('day').toDate();
      this.filtroFim = moment().endOf('day').toDate();
    } else if (preset === '90dias') {
      this.filtroInicio = moment().subtract(89, 'days').startOf('day').toDate();
      this.filtroFim = moment().endOf('day').toDate();
    } else {
      this.filtroInicio = moment().subtract(29, 'days').startOf('day').toDate();
      this.filtroFim = moment().endOf('day').toDate();
    }
  }

  private obtenhaRange(): string {
    if (this.quickDateFilter === 'hoje') return 'today';
    if (this.quickDateFilter === '7dias') return '7d';
    if (this.quickDateFilter === '14dias') return '14d';
    if (this.quickDateFilter === '90dias') return '90d';
    return '30d';
  }

  async carregueDashboard(): Promise<void> {
    if (this.carregando) return;
    this.carregando = true;
    this.erroMensagem = '';

    try {
      const params: any = {
        previous: true,
        interval: 'day'
      };

      params.startDate = moment(this.filtroInicio).format('YYYY-MM-DD');
      params.endDate = moment(this.filtroFim).format('YYYY-MM-DD');

      const dados = await this.analyticsService.obtenhaDashboard(params);
      const backendSemProjeto = dados?.metadados?.status === 'sem_projeto' || dados?.elegibilidade?.elegivel === false;

      if (backendSemProjeto) {
        this.dashboard = dados;
        this.metadados = dados?.metadados || {};
        this.status = 'sem_projeto';
        this.erroMensagem = '';
        return;
      }

      this.dashboard = dados;
      this.analyticsResumo = dados?.analytics || {};
      this.resumoFunil = dados.resumo_funil || {};
      this.steps = this.resumoFunil.steps || [];
      this.comparativo = dados.comparativo || {};
      this.serieTemporal = dados.serie_temporal || {};
      this.produtos = dados.produtos || [];
      this.campanhas = dados.campanhas || {};
      this.alertas = dados.alertas || [];
      this.metadados = dados.metadados || {};

      this.calculeGargalos();
      this.calculeTopRankings();
      this.calculeSerieConversao();
      this.calculeBannerResumo();
      this.status = 'pronto';
    } catch (erro: any) {
      this.erroMensagem = typeof erro === 'string' ? erro : 'Erro ao carregar dados do dashboard.';
      if (!this.dashboard) {
        this.status = 'erro';
      }
    } finally {
      this.carregando = false;
    }
  }

  // ---- Dados derivados ----

  private calculeBannerResumo(): void {
    const conversao = this.resumoFunil?.conversion || 0;
    const mapaRange: any = {
      hoje: 'hoje',
      '7dias': '7 dias',
      '14dias': '14 dias',
      '30dias': '30 dias',
      '90dias': '90 dias'
    };
    const range = mapaRange[this.quickDateFilter] || '30 dias';
    const maiorGargalo = this.gargalos.length ? this.gargalos[0] : null;

    let variacao = '';
    if (this.comparativo?.disponivel && this.comparativo.periodoAnterior) {
      const diff = conversao - (this.comparativo.periodoAnterior.conversion || 0);
      variacao = ` ${diff >= 0 ? '↑' : '↓'} ${Math.abs(diff).toFixed(1)}pp vs. período anterior.`;
    }

    let texto = `Nos últimos ${range}, a conversão do cardápio foi de ${conversao.toFixed(1)}%${variacao}`;
    if (maiorGargalo) {
      texto += ` Maior gargalo: ${maiorGargalo.de} → ${maiorGargalo.para} (queda de ${Math.abs(maiorGargalo.dropoffPercent).toFixed(1)}%).`;
    }
    this.bannerResumo = texto;
  }

  private calculeGargalos(): void {
    this.gargalos = [];
    for (let i = 0; i < this.steps.length - 1; i++) {
      const atual = this.steps[i];
      const proximo = this.steps[i + 1];
      const queda = atual.dropoffPercent != null
        ? Number(atual.dropoffPercent)
        : (
          atual.count > 0
            ? Number((((atual.count - proximo.count) / atual.count) * 100).toFixed(2))
            : 0
        );
      const passagem = atual.count > 0
        ? Number(((Number(proximo.count || 0) / Number(atual.count || 0)) * 100).toFixed(2))
        : 0;

      this.gargalos.push({
        de: atual.label,
        para: proximo.label,
        transitionPercent: passagem,
        dropoffPercent: queda,
        dropoffCount: atual.count - proximo.count
      });
    }
    this.gargalos.sort((a: any, b: any) => a.transitionPercent - b.transitionPercent);
  }

  private calculeTopRankings(): void {
    this.topVisualizacoes = [...this.produtos].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 5);
    this.topSacola = [...this.produtos].sort((a, b) => (b.addToCart || 0) - (a.addToCart || 0)).slice(0, 5);
    this.topPedidos = [...this.produtos].sort((a, b) => (b.pedidos || 0) - (a.pedidos || 0)).slice(0, 5);
  }

  private calculeSerieConversao(): void {
    const series = this.serieTemporal?.series || [];
    this.categoriesConversao = series.map((s: any) => {
      const d = moment(s.date || s.day || s.label);
      return d.isValid() ? d.format('DD/MM') : '';
    });
    this.seriesConversao = series.map((s: any) => {
      if (s?.conversion != null && Number.isFinite(Number(s.conversion))) {
        const total = Number(s.visitors || s.sessions || s.count || 0);
        const completed = Number(s.completed || s.orders || 0);

        if (total <= 0 && completed <= 0) {
          return null;
        }

        return Number(s.conversion);
      }

      const total = Number(s.visitors || s.sessions || s.count || 0);
      const completed = Number(s.completed || s.orders || 0);
      return total > 0 ? Number(((completed / total) * 100).toFixed(2)) : null;
    });
    this.seriesConversaoPlot = this.seriesConversao.map((valor: number | null) => valor == null ? 0 : valor);
  }

  temDadosSerieConversao(): boolean {
    return this.seriesConversao.some((valor: number | null) => valor != null);
  }

  obtenhaPontoSerieTemporalPorCategoria(categoria: string): any | null {
    const series = this.serieTemporal?.series || [];

    return series.find((item: any) => {
      const d = moment(item.date || item.day || item.label);
      return d.isValid() && d.format('DD/MM') === categoria;
    }) || null;
  }

  obtenhaTooltipConversao(categoria: string): { data: string; conversion: string; pedidos: number } {
    const ponto = this.obtenhaPontoSerieTemporalPorCategoria(categoria);
    const data = ponto?.date ? this.formateDataTooltip(ponto.date) : categoria;
    const visitantes = Number(ponto?.visitors || ponto?.sessions || ponto?.count || 0);
    const pedidos = Number(ponto?.completed || ponto?.orders || 0);
    const conversion = Number.isFinite(Number(ponto?.conversion))
      ? `${Number(ponto.conversion).toFixed(2)}%`
      : (visitantes > 0 ? `${((pedidos / visitantes) * 100).toFixed(2)}%` : '0,00%');

    return {
      data,
      conversion,
      pedidos
    };
  }

  private formateDataTooltip(data: string): string {
    const m = moment(data).locale('pt-br');
    if (!m.isValid()) {
      return data;
    }

    const texto = m.format('ddd DD/MM');
    return texto.charAt(0).toUpperCase() + texto.slice(1);
  }

  obtenhaMaximoConversaoGrafico(): number {
    const maximo = Math.max(...this.seriesConversaoPlot, 0);

    if (maximo <= 5) return 5;
    if (maximo <= 10) return 10;
    if (maximo <= 20) return 20;
    if (maximo <= 40) return 40;
    if (maximo <= 60) return 60;

    return 100;
  }

  obtenhaMajorUnitConversaoGrafico(): number {
    const maximo = this.obtenhaMaximoConversaoGrafico();

    if (maximo <= 5) return 1;
    if (maximo <= 10) return 2;
    if (maximo <= 20) return 5;

    return 10;
  }

  obtenhaStepRotuloConversao(): number {
    const total = this.categoriesConversao.length;

    if (total <= 7) return 1;
    if (total <= 14) return 2;
    if (total <= 31) return 5;
    if (total <= 60) return 7;

    return 10;
  }

  private obtenhaConteudoRotuloConversao(e: any): string {
    const total = this.categoriesConversao.length;

    if (!total) {
      return '';
    }

    const index = Number(e?.index || 0);
    const ultimoIndice = total - 1;

    if (index === 0 || index === ultimoIndice) {
      return e?.text || '';
    }

    const step = this.obtenhaStepRotuloConversao();

    if (step <= 1 || index % step !== 0) {
      return '';
    }

    const distanciaDoFim = ultimoIndice - index;
    const distanciaMinimaParaUltimoRotulo = Math.ceil(step / 2);

    return distanciaDoFim > distanciaMinimaParaUltimoRotulo ? (e?.text || '') : '';
  }

  // ---- Helpers para template ----

  obtenhaIconeStep(key: string): string {
    const icones: any = {
      visitas: 'fas fa-globe',
      visualizacoes: 'fas fa-mobile-alt',
      sacola: 'fas fa-shopping-bag',
      revisao: 'fas fa-clipboard-check',
      concluidos: 'fas fa-check-circle'
    };
    return icones[key] || 'fas fa-circle';
  }

  obtenhaCorStep(key: string): string {
    const cores: any = {
      visitas: '#3B82F6',
      visualizacoes: '#8B5CF6',
      sacola: '#F59E0B',
      revisao: '#F97316',
      concluidos: '#10B981'
    };
    return cores[key] || '#6C5CE7';
  }

  obtenhaCorStepFinal(key: string): string {
    const cores: any = {
      visitas: '#2563EB',
      visualizacoes: '#7C3AED',
      sacola: '#D97706',
      revisao: '#EA580C',
      concluidos: '#059669'
    };
    return cores[key] || '#5B4BD5';
  }

  obtenhaDescricaoStep(key: string): string {
    const descricoes: any = {
      visitas: 'visitaram seu cardápio',
      visualizacoes: 'visualizaram algum item',
      sacola: 'adicionaram itens na sacola',
      revisao: 'revisaram o pedido',
      concluidos: 'concluíram o pedido'
    };
    return descricoes[key] || '';
  }

  obtenhaDropoffParaStep(index: number): number | null {
    if (index <= 0 || index >= this.steps.length) return null;
    const anterior = this.steps[index - 1];
    const atual = this.steps[index];
    if (!anterior || !atual || anterior.count <= 0) return null;
    return ((anterior.count - atual.count) / anterior.count) * 100;
  }

  obtenhaVariacaoEtapaAnterior(index: number): number | null {
    if (index <= 0 || index >= this.steps.length) {
      return null;
    }

    const atual = Number(this.steps[index]?.count || 0);
    const anterior = Number(this.steps[index - 1]?.count || 0);

    if (anterior <= 0) {
      return null;
    }

    return ((atual - anterior) / anterior) * 100;
  }

  obtenhaTextoVariacaoEtapaAnterior(index: number): string | null {
    const variacao = this.obtenhaVariacaoEtapaAnterior(index);

    if (variacao == null) {
      return null;
    }

    if (Math.abs(variacao) < 0.05) {
      return '0,0% igual';
    }

    const positivo = variacao > 0;
    return `${Math.abs(variacao).toFixed(1)}% ${positivo ? 'acima' : 'abaixo'}`;
  }

  obtenhaIconeVariacaoEtapaAnterior(index: number): string {
    const variacao = this.obtenhaVariacaoEtapaAnterior(index);

    if (variacao == null || Math.abs(variacao) < 0.05) {
      return 'fa-minus';
    }

    return variacao > 0 ? 'fa-caret-up' : 'fa-caret-down';
  }

  obtenhaUnidadeStep(key: string): string {
    const unidades: any = {
      visitas: 'sessões',
      visualizacoes: 'eventos',
      sacola: 'sessões',
      revisao: 'sessões',
      concluidos: 'pedidos'
    };
    return unidades[key] || '';
  }

  obtenhaStepPeriodoAnterior(step: any): any | null {
    const steps = this.comparativo?.periodoAnterior?.steps;

    if (!Array.isArray(steps) || !step) {
      return null;
    }

    return steps.find((item: any) => item?.key === step?.key || item?.event === step?.event) || null;
  }

  obtenhaVariacaoStep(step: any): number | null {
    const stepAnterior = this.obtenhaStepPeriodoAnterior(step);
    const valorAnterior = Number(stepAnterior?.count || 0);

    if (!stepAnterior || valorAnterior <= 0) {
      return null;
    }

    return ((Number(step?.count || 0) - valorAnterior) / valorAnterior) * 100;
  }

  obtenhaTextoVariacaoStep(step: any): string | null {
    const variacao = this.obtenhaVariacaoStep(step);

    if (variacao == null) {
      return null;
    }

    if (Math.abs(variacao) < 0.05) {
      return '0,0% igual';
    }

    const positivo = variacao >= 0;
    return `${positivo ? '+' : '-'}${Math.abs(variacao).toFixed(1)}% ${positivo ? 'acima' : 'abaixo'}`;
  }

  obtenhaIconeVariacaoStep(step: any): string {
    const variacao = this.obtenhaVariacaoStep(step);

    if (variacao == null || Math.abs(variacao) < 0.05) {
      return 'fa-minus';
    }

    return variacao > 0 ? 'fa-caret-up' : 'fa-caret-down';
  }

  obtenhaClasseVariacaoStep(step: any): 'positiva' | 'negativa' | 'neutra' {
    const variacao = this.obtenhaVariacaoStep(step);

    if (variacao == null || Math.abs(variacao) < 0.05) {
      return 'neutra';
    }

    return variacao > 0 ? 'positiva' : 'negativa';
  }

  obtenhaPercentualResumo(index: number): string {
    const percentual = Number(this.steps?.[index]?.percent || 0);

    if (Math.abs(percentual - 100) < 0.005) {
      return '100%';
    }

    return `${percentual.toFixed(2)}%`;
  }

  obtenhaPercentualResumoValor(index: number): number {
    return Number(this.steps?.[index]?.percent || 0);
  }

  private obtenhaNiveisResumo(index: number): { esquerdo: number; direito: number; atual: number } {
    const atual = this.obtenhaPercentualResumoValor(index);
    const anterior = index > 0
      ? this.obtenhaPercentualResumoValor(index - 1)
      : atual;
    const proximo = index + 1 < this.steps.length
      ? this.obtenhaPercentualResumoValor(index + 1)
      : atual;

    const esquerdo = index === 0 ? atual : (anterior + atual) / 2;
    const direito = index === this.steps.length - 1 ? atual : (atual + proximo) / 2;

    return { esquerdo, direito, atual };
  }

  obtenhaClassePercentualResumo(index: number): string {
    return this.obtenhaPercentualResumoValor(index) < 30 ? 'escuro' : 'claro';
  }

  obtenhaWaveClipPath(index: number): string {
    const atual = Number(this.steps?.[index]?.percent || 0);
    const proximo = Number(this.steps?.[index + 1]?.percent ?? atual);
    const topoAtual = Math.max(0, Math.min(100, 100 - atual));
    const topoProximo = Math.max(0, Math.min(100, 100 - proximo));

    return `polygon(0 ${topoAtual}%, 100% ${topoProximo}%, 100% 100%, 0 100%)`;
  }

  obtenhaWavePath(index: number): string {
    const niveis = this.obtenhaNiveisResumo(index);
    const topoEsquerdo = Math.max(8, Math.min(86, 100 - niveis.esquerdo));
    const topoDireito = Math.max(8, Math.min(86, 100 - niveis.direito));

    return `M 0 100 L 0 ${topoEsquerdo} L 100 ${topoDireito} L 100 100 Z`;
  }

  obtenhaOffsetPercentualResumo(index: number): string {
    const niveis = this.obtenhaNiveisResumo(index);
    const nivelTexto = (niveis.esquerdo + niveis.atual + niveis.direito) / 3;
    const topo = 100 - nivelTexto;
    const percentualAtual = this.obtenhaPercentualResumoValor(index);

    if (percentualAtual < 30) {
      const posicaoAcimaBarra = Math.max(24, Math.min(58, topo - 10));
      return `${posicaoAcimaBarra}%`;
    }

    const posicao = Math.max(34, Math.min(78, topo + (nivelTexto * 0.45)));

    return `${posicao}%`;
  }

  obtenhaFusoHorario(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return 'America/Sao_Paulo';
    }
  }

  obtenhaBarWidth(dropoffPercent: number): number {
    return Math.min(Math.max(dropoffPercent, 0), 100);
  }

  obtenhaBarColor(dropoffPercent: number): string {
    if (dropoffPercent >= 90) return '#22C55E';
    if (dropoffPercent >= 70) return '#84CC16';
    if (dropoffPercent >= 40) return '#F59E0B';
    return '#EF4444';
  }

  obtenhaCardsAnalytics(): Array<{
    key: string;
    label: string;
    valor: number;
    descricao: string;
    variacao: string | null;
  }> {
    const analytics = this.analyticsResumo || {};

    return [
      {
        key: 'sessoes',
        label: 'Sessões',
        valor: Number(analytics.sessoes || 0),
        descricao: 'sessões identificadas no período',
        variacao: this.obtenhaTextoVariacaoAnalytics('sessoes')
      },
      {
        key: 'visitas',
        label: 'Visitas',
        valor: Number(analytics.visitas || 0),
        descricao: 'eventos de entrada no cardápio',
        variacao: this.obtenhaTextoVariacaoAnalytics('visitas')
      },
      {
        key: 'visitasUnicas',
        label: 'Visitas únicas',
        valor: Number(analytics.visitasUnicas || 0),
        descricao: 'sessões únicas com visita ao cardápio',
        variacao: this.obtenhaTextoVariacaoAnalytics('visitasUnicas')
      },
      {
        key: 'visualizacoes',
        label: 'Visualizações',
        valor: Number(analytics.visualizacoes || 0),
        descricao: 'eventos de visualização de produto',
        variacao: this.obtenhaTextoVariacaoAnalytics('visualizacoes')
      },
      {
        key: 'visualizacoesUnicas',
        label: 'Visualizações únicas',
        valor: Number(analytics.visualizacoesUnicas || 0),
        descricao: 'sessões únicas com product_view',
        variacao: this.obtenhaTextoVariacaoAnalytics('visualizacoesUnicas')
      },
      {
        key: 'pedidosConcluidos',
        label: 'Pedidos concluídos',
        valor: Number(analytics.pedidosConcluidos || 0),
        descricao: 'pedidos deduplicados no período',
        variacao: this.obtenhaTextoVariacaoAnalytics('pedidosConcluidos')
      }
    ];
  }

  private obtenhaValorPeriodoAnteriorAnalytics(campo: string): number | null {
    const periodoAnterior = this.analyticsResumo?.periodoAnterior;

    if (!periodoAnterior || periodoAnterior[campo] == null) {
      return null;
    }

    return Number(periodoAnterior[campo] || 0);
  }

  obtenhaTextoVariacaoAnalytics(campo: string): string | null {
    const analytics = this.analyticsResumo || {};
    const atual = Number(analytics[campo] || 0);
    const anterior = this.obtenhaValorPeriodoAnteriorAnalytics(campo);

    if (anterior == null) {
      return null;
    }

    if (anterior <= 0) {
      return atual > 0 ? 'novo no período' : 'sem mudança';
    }

    const variacao = ((atual - anterior) / anterior) * 100;

    if (Math.abs(variacao) < 0.05) {
      return '0,0% igual';
    }

    return `${variacao > 0 ? '+' : '-'}${Math.abs(variacao).toFixed(1)}% vs. anterior`;
  }

  exportarCSV(tipo: string = 'funnel'): void {
    const url = this.analyticsService.obtenhaUrlExportacao(tipo, {
      range: this.obtenhaRange(),
      startDate: this.quickDateFilter === 'personalizado' && this.filtroInicio
        ? moment(this.filtroInicio).format('YYYY-MM-DD') : undefined,
      endDate: this.quickDateFilter === 'personalizado' && this.filtroFim
        ? moment(this.filtroFim).format('YYYY-MM-DD') : undefined
    });
    window.open(url, '_blank');
  }

  temIframesOpenpanel(): boolean {
    return !!(this.openpanelDashboardIframe || this.openpanelRealtimeIframe);
  }

  private atualizeIframesOpenpanel(empresa: any): void {
    const reports = Array.isArray(empresa?.openpanelReports)
      ? empresa.openpanelReports
      : (Array.isArray(empresa?.openpanel?.reports) ? empresa.openpanel.reports : []);

    const overview = reports.find((report: any) => report?.id === 'overview' && report?.url)
      || reports.find((report: any) => String(report?.tipo || '').toLowerCase() !== 'widget' && report?.url)
      || null;

    const realtime = reports.find((report: any) => report?.id === 'widget-realtime' && report?.url)
      || reports.find((report: any) => String(report?.tipo || '').toLowerCase() === 'widget' && report?.url)
      || null;

    const dashboardUrl = String(
      overview?.url
      || empresa?.openpanelDashboardUrl
      || empresa?.openpanel?.dashboardUrl
      || ''
    ).trim();
    const realtimeUrl = String(realtime?.url || '').trim();

    this.openpanelDashboardUrl = dashboardUrl;
    this.openpanelRealtimeUrl = realtimeUrl;
    this.openpanelDashboardIframe = this.convertaParaSafeIframeUrl(dashboardUrl);
    this.openpanelRealtimeIframe = this.convertaParaSafeIframeUrl(realtimeUrl);
  }

  private convertaParaSafeIframeUrl(url: string): SafeResourceUrl | null {
    const valor = String(url || '').trim();

    if (!valor) {
      return null;
    }

    try {
      const parsed = new URL(valor, window.location.origin);
      const host = parsed.hostname;
      const local = host === 'localhost' || host === '127.0.0.1';
      const protocoloValido = parsed.protocol === 'https:' || (parsed.protocol === 'http:' && local);

      if (!protocoloValido) {
        return null;
      }

      return this.sanitizer.bypassSecurityTrustResourceUrl(parsed.toString());
    } catch {
      return null;
    }
  }
}
